import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { articles } from '../data/articles.js'
import SEO from '../components/SEO'

export default function ArticlePage() {
  const [allocatedStats, setAllocatedStats] = useState({})
  const { slug } = useParams()
  const navigate = useNavigate()
  const article = articles.find((a) => a.slug === slug)

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#050510' }}>
        <div className="text-center">
          <h1 className="font-display text-4xl font-bold text-nerd-white mb-4">404</h1>
          <p className="text-nerd-gray mb-6">Questo articolo non esiste nel database.</p>
          <button
            onClick={() => navigate('/blog')}
            className="text-nerd-cyan font-display uppercase tracking-wider text-sm hover:underline"
          >
            Torna al Blog
          </button>
        </div>
      </div>
    )
  }

  const currentIndex = articles.findIndex((a) => a.slug === slug)
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "datePublished": article.date,
    "author": {
      "@type": "Person",
      "name": "Amedeo Sbardellotto",
      "jobTitle": "Founder & Coach at Nerd Coach"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Nerd Coach",
      "logo": {
        "@type": "ImageObject",
        "url": "https://nerdcoach.it/favicon.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://nerdcoach.it/blog/${article.slug}`
    },
    "image": "https://nerdcoach.it/og-image.png"
  }

  const renderCustomBlock = (block, idx) => {
    switch (block.type) {
      case 'quote':
        return (
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative p-6 md:p-8 rounded-xl overflow-hidden border-l-4 border-nerd-purple bg-nerd-dark/65 backdrop-blur-md my-8 shadow-[0_0_20px_rgba(139,92,246,0.1)] font-body"
          >
            {/* Background big double quote */}
            <span className="absolute -top-6 -left-2 text-[120px] font-serif text-nerd-purple/10 pointer-events-none select-none">
              “
            </span>
            <p className="font-display text-lg md:text-xl font-bold text-nerd-white relative z-10 leading-relaxed italic">
              "{block.text}"
            </p>
            {block.author && (
              <p className="text-right text-nerd-cyan font-display uppercase tracking-widest text-xs mt-4 relative z-10">
                — {block.author}
              </p>
            )}
          </motion.div>
        );

      case 'quest':
        return (
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="p-6 md:p-8 rounded-xl border border-nerd-blue/30 bg-nerd-dark/80 my-8 relative overflow-hidden shadow-[0_0_25px_rgba(0,212,255,0.15)] font-body"
          >
            {/* Futuristic Tech Lines & Scanline Overlay */}
            <div className="absolute inset-0 bg-nerd-blue/[0.02] pointer-events-none" />
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-nerd-blue to-transparent animate-pulse" />
            
            <div className="flex items-center gap-3 mb-4">
              <span className="flex h-3 w-3 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-nerd-blue opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-nerd-blue"></span>
              </span>
              <h3 className="font-display font-bold text-nerd-blue text-sm uppercase tracking-widest">
                [!] {block.title || 'QUEST ATTIVA'}
              </h3>
            </div>

            <div className="space-y-3 pl-6 border-l border-nerd-blue/20">
              {block.objectives?.map((obj, oIdx) => (
                <div key={oIdx} className="flex items-start gap-3">
                  <span className="text-nerd-blue font-bold">▶</span>
                  <span className="text-nerd-white text-sm md:text-base font-body">{obj}</span>
                </div>
              ))}
            </div>

            {block.penalty && (
              <div className="mt-5 p-3 rounded bg-red-950/20 border border-nerd-red/35">
                <span className="text-nerd-red font-display font-bold text-xs uppercase block mb-1">
                  PENALITÀ DI INCOMPLETAMENTO:
                </span>
                <p className="text-nerd-gray text-xs md:text-sm font-body">
                  {block.penalty}
                </p>
              </div>
            )}
          </motion.div>
        );

      case 'stats': {
        const statsKey = block.title || 'default-stats';
        const allocated = allocatedStats[statsKey] || { points: 3, values: {} };
        const hasLevelUp = allocated.points === 0;

        const handleAddPoint = (statName) => {
          if (allocated.points <= 0) return;
          const currentVal = allocated.values[statName] || 0;
          setAllocatedStats({
            ...allocatedStats,
            [statsKey]: {
              points: allocated.points - 1,
              values: {
                ...allocated.values,
                [statName]: currentVal + 1
              }
            }
          });
        };

        return (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 md:p-8 rounded-xl border border-nerd-cyan/30 bg-nerd-dark/85 my-8 shadow-[0_0_30px_rgba(103,232,249,0.15)] relative overflow-hidden font-body"
          >
            {/* Holographic background look */}
            <div className="absolute inset-0 bg-gradient-to-br from-nerd-cyan/[0.01] to-transparent pointer-events-none" />
            
            <div className="flex justify-between items-center border-b border-nerd-cyan/20 pb-4 mb-6">
              <h3 className="font-display font-bold text-gradient text-sm md:text-base tracking-widest uppercase">
                {block.title || 'SCHERMATA STATISTICHE'}
              </h3>
              <div className="text-right font-display">
                <span className="text-xs text-nerd-gray/60 block">PUNTI ATTRIBUTO:</span>
                <span className={`font-bold text-lg ${allocated.points > 0 ? 'text-nerd-gold animate-pulse' : 'text-nerd-cyan'}`}>
                  {allocated.points}
                </span>
              </div>
            </div>

            {/* Regole & Contesto dell'autovalutazione */}
            <div className="mb-6 p-3.5 rounded-lg bg-nerd-cyan/5 border border-nerd-cyan/15 text-[11px] md:text-xs leading-relaxed text-nerd-gray/90">
              <span className="text-nerd-cyan font-display font-bold uppercase tracking-wider block mb-1">🎮 ISTRUZIONI DELLA QUEST:</span>
              Analizza sinceramente la tua situazione rispetto a queste statistiche. Hai a disposizione <strong className="text-nerd-gold">3 Punti Attributo</strong>. Clicca sui pulsanti <strong className="text-nerd-gold">+</strong> per assegnarli alle aree su cui senti il bisogno di lavorare oggi o per potenziare i tuoi punti di forza. Completa l'allocazione per sbloccare il <strong className="text-nerd-cyan">Level Up</strong>!
            </div>

            <div className="space-y-4">
              {block.stats?.map((stat, sIdx) => {
                const added = allocated.values[stat.name] || 0;
                const baseVal = stat.value || 10;
                const totalVal = baseVal + added;
                
                return (
                  <div key={sIdx} className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-display font-bold text-nerd-white tracking-wide">
                        {stat.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="font-display text-nerd-cyan font-bold">
                          {totalVal} {added > 0 && <span className="text-nerd-gold text-xs">(+{added})</span>}
                        </span>
                        {allocated.points > 0 && (
                          <button
                            onClick={() => handleAddPoint(stat.name)}
                            className="h-6 w-6 rounded border border-nerd-gold/45 text-nerd-gold hover:bg-nerd-gold/15 active:scale-95 transition-all flex items-center justify-center font-bold text-sm font-display"
                          >
                            +
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="h-1.5 w-full bg-nerd-bg rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-nerd-blue to-nerd-cyan transition-all duration-500"
                        style={{ width: `${Math.min(100, (totalVal / 30) * 100)}%` }}
                      />
                    </div>
                    {stat.desc && <span className="text-[11px] text-nerd-gray/50 block font-body">{stat.desc}</span>}
                  </div>
                );
              })}
            </div>

            <AnimatePresence>
              {hasLevelUp && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-6 pt-4 border-t border-nerd-cyan/15 text-center"
                >
                  <span className="font-display text-xs md:text-sm font-bold text-nerd-gold animate-bounce block tracking-widest">
                    ★ LEVEL UP COMPILATO CON SUCCESSO! ★
                  </span>
                  <p className="text-nerd-gray text-[11px] font-body mt-1">
                    Hai allocato tutti i punti attributo. Ottimizzazione hardware completata.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      }

      case 'loot':
        return (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 md:p-8 rounded-xl border border-nerd-gold/30 bg-nerd-dark/80 my-8 shadow-[0_0_25px_rgba(250,204,21,0.15)] relative overflow-hidden font-body"
          >
            {/* Subtle glow border */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-nerd-gold/5 rounded-full filter blur-xl pointer-events-none" />
            
            <div className="flex items-center gap-3 border-b border-nerd-gold/20 pb-4 mb-6">
              <span className="text-lg">🏆</span>
              <h3 className="font-display font-bold text-nerd-gold text-sm md:text-base uppercase tracking-widest">
                {block.title || 'RICOMPENSE RICEVUTE (LOOT)'}
              </h3>
            </div>

            <div className="space-y-4">
              {block.items?.map((item, iIdx) => {
                let rarityColor = 'text-nerd-gray border-nerd-gray/25';
                let rarityBg = 'bg-nerd-gray/5';
                if (item.rarity?.toLowerCase() === 'leggendaria' || item.rarity?.toLowerCase() === 'leggendario') {
                  rarityColor = 'text-nerd-gold border-nerd-gold/40 animate-pulse font-bold';
                  rarityBg = 'bg-nerd-gold/5';
                } else if (item.rarity?.toLowerCase() === 'epica' || item.rarity?.toLowerCase() === 'epico') {
                  rarityColor = 'text-nerd-purple border-nerd-purple/40 font-bold';
                  rarityBg = 'bg-nerd-purple/5';
                } else if (item.rarity?.toLowerCase() === 'rara' || item.rarity?.toLowerCase() === 'raro') {
                  rarityColor = 'text-nerd-blue border-nerd-blue/40';
                  rarityBg = 'bg-nerd-blue/5';
                }

                return (
                  <div key={iIdx} className={`p-3 rounded-lg border ${rarityColor} ${rarityBg} flex flex-col md:flex-row md:justify-between md:items-center gap-2`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs uppercase px-2 py-0.5 rounded border border-current font-display text-[9px]">
                          {item.rarity}
                        </span>
                        <span className="font-display text-sm font-bold text-nerd-white">
                          {item.name}
                        </span>
                      </div>
                      {item.effect && (
                        <p className="text-xs text-nerd-gray/70 mt-1 pl-1 font-body">
                          Effetto passivo: {item.effect}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen px-4 pt-32 pb-24" style={{ background: '#050510' }}>
      <SEO 
        title={article.title}
        description={article.excerpt}
        type="article"
        schema={articleSchema}
      />
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/blog"
            className="inline-flex items-center text-nerd-gray hover:text-nerd-cyan transition-colors text-sm mb-8"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Torna al Blog
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-nerd-cyan text-xs font-display uppercase tracking-wider">
              {article.date}
            </span>
            <span className="text-nerd-gray/50 text-xs">·</span>
            <span className="text-nerd-gray/50 text-xs">{article.readTime} di lettura</span>
          </div>

          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gradient mb-6 leading-tight">
            {article.title}
          </h1>

          {article.image && (
            <div className="relative rounded-xl overflow-hidden mb-10 border border-nerd-purple/20 shadow-[0_0_30px_rgba(139,92,246,0.15)] h-56 sm:h-72 md:h-[400px] w-full">
              <img
                src={article.image}
                alt={article.title}
                fetchpriority="high"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050510] via-transparent to-[#050510]/20 pointer-events-none" />
            </div>
          )}

          <p className="text-nerd-gray text-lg leading-relaxed mb-10 border-l-2 border-nerd-cyan pl-4">
            {article.excerpt}
          </p>

          <div className="flex flex-wrap gap-2 mb-12">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-3 py-1 rounded-full border border-nerd-purple/20 text-nerd-gray/70"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="space-y-12">
            {article.content.map((section, idx) => (
              <motion.section
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
              >
                <h2 className="font-display text-xl md:text-2xl font-bold text-nerd-white mb-4">
                  {section.heading}
                </h2>
                <div className="space-y-6">
                  {section.paragraphs.map((paragraph, pIdx) => {
                    if (typeof paragraph === 'object' && paragraph !== null) {
                      return renderCustomBlock(paragraph, pIdx);
                    }
                    return (
                      <p
                        key={pIdx}
                        className="text-nerd-gray leading-relaxed text-base md:text-lg"
                      >
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
              </motion.section>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-nerd-purple/10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              {prevArticle ? (
                <Link
                  to={`/blog/${prevArticle.slug}`}
                  className="group flex items-center text-nerd-gray hover:text-nerd-cyan transition-colors"
                >
                  <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <div>
                    <span className="text-xs text-nerd-gray/50 block">Precedente</span>
                    <span className="text-sm font-display">{prevArticle.title}</span>
                  </div>
                </Link>
              ) : (
                <div />
              )}

              {nextArticle ? (
                <Link
                  to={`/blog/${nextArticle.slug}`}
                  className="group flex items-center text-right text-nerd-gray hover:text-nerd-cyan transition-colors"
                >
                  <div>
                    <span className="text-xs text-nerd-gray/50 block">Successivo</span>
                    <span className="text-sm font-display">{nextArticle.title}</span>
                  </div>
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              ) : (
                <div />
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
